import React, { useEffect, useMemo, useState, useRef } from "react";
import { UserButton, useUser, useClerk } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import {
  Play,
  Pause,
  Sparkles,
  Database,
  Cpu,
  Trophy,
  Zap,
  BookOpen,
  Server,
  Skull,
  CheckCircle,
  ArrowRight,
  Twitter,
  Github,
  Linkedin,
  Gift,
  Star,
  ShieldCheck,
  ChevronDown,
  Sun,
  Moon,
  Maximize2,
  Minimize2,
  X,
  Crown,
  Video
} from "lucide-react";

/* ------------------------------------------------------------ */
/* COMPONENT: NEON GLOW CARD (Theme Aware)                     */
/* ------------------------------------------------------------ */
const NeonGradientCard = ({ children, className = "", borderColors, isDark }) => {
  // Default Border Colors based on theme if not provided
  const borders = borderColors || (isDark
    ? "from-cyan-400 via-fuchsia-500 to-yellow-500"
    : "from-cyan-500 via-fuchsia-500 to-yellow-500");

  return (
    <div className={`relative group ${className}`}>
      {/* Glow Effect */}
      <div
        className={`absolute -inset-[1px] bg-gradient-to-r ${borders} rounded-2xl transition duration-500
        ${isDark ? "opacity-50 blur-sm group-hover:opacity-100 group-hover:blur-md" : "opacity-40 blur-md group-hover:opacity-70 group-hover:blur-xl"}`}
      />

      {/* Sharp Border */}
      <div className={`absolute -inset-[1px] bg-gradient-to-r ${borders} rounded-2xl opacity-100`} />

      {/* Inner Content Background */}
      <div className={`relative h-full w-full rounded-2xl overflow-hidden transition-colors duration-500
        ${isDark ? "bg-slate-950" : "bg-white"}`}>
        {children}
      </div>
    </div>
  );
};

/* ---------------- DATA CONSTANTS ---------------- */
const SQL_TERMS_POOL = [
  ["SELECT", "COUNT(*)", "FROM users"],
  ["JOIN", "ORDER BY", "WHERE status='A'"],
  ["GROUP BY", "SUM(sales)", "HAVING > 1"],
  ["UPDATE", "SET email=", "WHERE id=1"],
  ["INSERT", "INTO table", "VALUES ()"],
];

const TESTIMONIALS = [
  {
    name: "Akash R.",
    role: "Commerce Student",
    quote:
      "The thrill of the spin and the challenge of the query made learning SQL natural. I redeemed my coins for a full SQL workshop.",
    outcome: "Cracked 2 SQL interviews in 3 weeks",
  },
  {
    name: "Neha G.",
    role: "Business Analyst",
    quote:
      "It felt like a game, but the rewards are real—I unlocked a Python analytics session that directly helped my job.",
    outcome: "Moved into a Data Analyst role",
  },
  {
    name: "Rohit D.",
    role: "Aspiring Data Engineer",
    quote:
      "The advanced spins immediately exposed my weak SQL areas. It’s the perfect, addictive learning hook.",
    outcome: "Levelled up joins + window functions",
  },
  {
    name: "Pooja K.",
    role: "Product Analyst",
    quote:
      "I used my winnings to access GenAI content. The combination of gaming and career rewards is genius.",
    outcome: "Shipped a mini RAG project",
  },
];

const FAQS = [
  {
    question: "What is DataSense Casino Royale?",
    answer:
      "It’s a gamified learning experience where SQL keywords act as slot reels. You bet virtual chips, spin the reels, and then prove you understand the resulting query to win CP, unlock upgrades, and progress through missions.",
  },
  {
    question: "Is real money involved?",
    answer:
      "No. DataSense Casino is purely educational. The '$' sign represents in-game score only. You cannot deposit or withdraw real currency.",
  },
  {
    question: "How do Boss Battles work?",
    answer:
      "Every few levels, you face a Boss (e.g., 'The Infinite Loop'). You must answer questions correctly to drain their HP before they punish your bankroll. Boss fights are designed to expose weak areas and build speed.",
  },
  {
    question: "What is the Rig System?",
    answer:
      "Your Rig is your permanent progression engine. You buy virtual hardware (CPU, GPU, RAM, PSU) that boosts multipliers, luck, refunds, and discounts. Upgrades affect every spin, so smart players snowball fast.",
  },
  {
    question: "Who is this for?",
    answer:
      "Beginners who want a fun entry into SQL, analysts who want consistency, and interview-focused learners who want practice without boredom. It’s built to keep you engaged long enough to actually master concepts.",
  },
];

/* ---------------- MAIN COMPONENT ---------------- */
function LandingPage() {
  const navigate = useNavigate();
  const { isSignedIn } = useUser();
  const { openSignIn } = useClerk();
  const [isDark, setIsDark] = useState(false); // Theme State
  const [reels, setReels] = useState(SQL_TERMS_POOL[0]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(FAQS[0]?.question ?? null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHoveringSlot, setIsHoveringSlot] = useState(false);

  // Video Player State
  const videoRef = useRef(null);
  const playerContainerRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Helper function to switch styles based on theme
  // t(darkModeClass, lightModeClass)
  const t = (darkClass, lightClass) => isDark ? darkClass : lightClass;

  const marqueeItems = useMemo(
    () => [
      { icon: <Sparkles size={12} />, text: "Dev_Mike hit a $50,000 JACKPOT" },
      { icon: <Skull size={12} />, text: "Sarah_SQL defeated The Infinite Loop" },
      { icon: <Cpu size={12} />, text: "AlexCodes installed RTX 4090 GPU" },
      { icon: <Trophy size={12} />, text: "PoojaK claimed a Mission Bounty" },
      { icon: <Gift size={12} />, text: "New Daily Rewards available now" },
    ],
    []
  );

  /* --- SPIN LOGIC --- */
  const spinSlot = () => {
    if (isSpinning) return;
    setIsSpinning(true);

    const interval = setInterval(() => {
      setReels(SQL_TERMS_POOL[Math.floor(Math.random() * SQL_TERMS_POOL.length)]);
    }, 120);

    setTimeout(() => {
      clearInterval(interval);
      setReels(SQL_TERMS_POOL[Math.floor(Math.random() * SQL_TERMS_POOL.length)]);
      setIsSpinning(false);
    }, 1100);
  };

  /* --- VIDEO LOGIC --- */
  const toggleVideo = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  const handleStopVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsVideoPlaying(false);
    }
    // If we are in fullscreen, exit it
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(err => console.log(err));
      setIsFullscreen(false);
    }
  };

  const toggleFullscreen = () => {
    if (!playerContainerRef.current) return;

    if (!document.fullscreenElement) {
      playerContainerRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  // Listen for native escape key exiting fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);


  /* --- AUTO SPIN ON LOAD --- */
  useEffect(() => {
    spinSlot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartGame = () => navigate("/game");

  return (
    <div className={`min-h-screen overflow-x-hidden font-sans transition-colors duration-500 
      ${t("bg-slate-950 text-slate-50 selection:bg-yellow-500/30 selection:text-yellow-200",
      "bg-slate-50 text-slate-900 selection:bg-fuchsia-200 selection:text-fuchsia-900")}`}>

      {/* --- BACKGROUNDS --- */}
      <div className="fixed inset-0 -z-10 pointer-events-none transition-opacity duration-700">
        {isDark ? (
          // DARK THEME BG
          <>
            <div className="absolute inset-0 bg-[radial-gradient(1200px_900px_at_30%_15%,rgba(236,72,153,0.38),transparent_60%),radial-gradient(1000px_800px_at_78%_22%,rgba(34,211,238,0.35),transparent_58%),radial-gradient(900px_700px_at_55%_82%,rgba(250,204,21,0.28),transparent_62%),linear-gradient(180deg,#140016_0%,#070014_40%,#04020a_100%)]" />
            <div className="absolute -top-40 left-1/2 h-[900px] w-[900px] -translate-x-1/2 rounded-full blur-3xl opacity-40 animate-[spin_40s_linear_infinite] bg-[conic-gradient(from_90deg,rgba(250,204,21,0.0),rgba(250,204,21,0.35),rgba(236,72,153,0.25),rgba(34,211,238,0.25),rgba(250,204,21,0.0))]" />
            <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "linear-gradient(to right, rgba(34,211,238,0.18) 1px, transparent 1px), linear-gradient(to bottom, rgba(236,72,153,0.12) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
          </>
        ) : (
          // LIGHT THEME BG
          <>
            <div className="absolute inset-0 bg-slate-50" />
            <div className="absolute top-0 left-0 w-[800px] h-[800px] bg-fuchsia-200/40 blur-[120px] rounded-full mix-blend-multiply opacity-60" />
            <div className="absolute bottom-0 right-0 w-[800px] h-[800px] bg-cyan-200/40 blur-[120px] rounded-full mix-blend-multiply opacity-60" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-yellow-200/30 blur-[100px] rounded-full mix-blend-multiply opacity-50" />
            <div className="absolute inset-0 opacity-[0.4]" style={{ backgroundImage: "linear-gradient(to right, rgba(203,213,225,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(203,213,225,0.4) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
          </>
        )}
      </div>

      {/* --- NAVBAR --- */}
      <nav className={`sticky top-0 z-50 w-full border-b backdrop-blur-xl transition-colors duration-300
        ${t("border-white/10 bg-black/60", "border-slate-200 bg-white/70")}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-3 group select-none">
            <div className="relative">
              <div className={`absolute -inset-2 rounded-2xl blur-lg transition-colors ${t("bg-fuchsia-500/20 group-hover:bg-fuchsia-500/30", "bg-fuchsia-400/20 group-hover:bg-fuchsia-400/30")}`} />
              <div className={`relative h-10 w-10 rounded-2xl border flex items-center justify-center transition-colors
                ${t("bg-gradient-to-br from-fuchsia-500/35 to-cyan-500/20 border-white/10", "bg-gradient-to-br from-slate-100 to-white border-slate-300")}`}>
                <Crown className={t("text-yellow-200", "text-yellow-500")} size={18} />
              </div>
            </div>

            <div className="flex flex-col leading-tight text-left">
              <span className={`text-[10px] font-black tracking-[0.35em] uppercase ${t("text-yellow-200/80", "text-slate-500")}`}>
                DataSense
              </span>
              <span className={`text-base md:text-lg font-black tracking-wider ${t("text-white", "text-slate-900")}`}>
                Casino{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 to-cyan-400">
                  Royale
                </span>
              </span>
            </div>
          </button>

          {/* Theme Toggle & Links */}
          <div className="flex items-center gap-6">
            <div className={`hidden md:flex items-center gap-6 text-[11px] font-black tracking-[0.25em] uppercase ${t("text-white/65", "text-slate-500")}`}>
              {["Guide", "Mechanics", "Roadmap"].map(item => (
                <button key={item} className={`transition-colors ${t("hover:text-yellow-200", "hover:text-fuchsia-600")}`}
                  onClick={() => item === "Guide" ? navigate("/instructions") : document.getElementById(item.toLowerCase())?.scrollIntoView({ behavior: "smooth" })}>
                  {item}
                </button>
              ))}
            </div>

            <button
              onClick={() => setIsDark(!isDark)}
              className={`p-2 rounded-xl transition-all ${t("bg-white/10 text-yellow-300 hover:bg-white/20", "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-orange-500")}`}
              title="Toggle Theme"
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <div className="flex items-center gap-3">
              {/* AUTH BUTTONS */}
              {isSignedIn ? (
                <UserButton
                  afterSignOutUrl="/"
                  appearance={{
                    elements: {
                      avatarBox: "w-9 h-9 ring-2 ring-fuchsia-500/50 hover:ring-fuchsia-500 transition-all"
                    }
                  }}
                />
              ) : (
                <button
                  onClick={() => openSignIn()}
                  className={`hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-xl border transition-colors ${t("border-white/10 bg-white/5 text-white/80 hover:bg-white/10", "border-slate-200 bg-white text-slate-600 hover:bg-slate-50")}`}
                >
                  <span className="text-[11px] font-black tracking-widest uppercase">
                    Login
                  </span>
                </button>
              )}


              <button
                onClick={handleStartGame}
                className="hidden sm:flex px-5 py-2 rounded-xl bg-gradient-to-r from-yellow-300 via-yellow-200 to-fuchsia-200 text-black font-black tracking-widest uppercase shadow-[0_0_20px_rgba(250,204,21,0.3)] hover:shadow-[0_0_30px_rgba(250,204,21,0.4)] active:scale-[0.99] transition-all items-center gap-2"
              >
                <Play size={16} /> Play
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="relative z-10 pb-28">
        {/* --- HERO --- */}
        <section className="pt-14 md:pt-20 pb-16 md:pb-24 px-4 md:px-6">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">

            {/* LEFT: Copy */}
            <div className="space-y-7">
              <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 backdrop-blur-xl ${t("border-yellow-300/20 bg-black/35", "border-emerald-200 bg-emerald-50")}`}>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className={`text-[10px] font-black tracking-[0.35em] uppercase ${t("text-yellow-200/80", "text-emerald-700")}`}>
                  v2.0 Live • Boss Battles • Daily Rewards • 50 Missions
                </span>
              </div>

              <h1 className={`text-4xl sm:text-5xl lg:text-7xl font-black leading-[1.02] ${t("text-white drop-shadow-2xl", "text-slate-900 drop-shadow-sm")}`}>
                Master SQL.
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-fuchsia-300 to-cyan-300">
                  Break the Bank.
                </span>
              </h1>

              <p className={`text-base md:text-lg leading-relaxed max-w-2xl ${t("text-white/75", "text-slate-600")}`}>
                DataSense Casino Royale is a{" "}
                <strong className={t("text-white", "text-slate-900")}>Database Casino RPG</strong> where
                you learn by playing. Every spin generates a real SQL query. Your
                job is to interpret it correctly, beat the question round, and
                turn chips into CP, upgrades, and momentum.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Zap, color: t("text-yellow-200", "text-yellow-600"), title: "Learn Faster", desc: "Repetition without boredom. The slot loop gives you endless query practice, so SELECT/JOIN/GROUP BY becomes muscle memory." },
                  { icon: ShieldCheck, color: t("text-cyan-200", "text-cyan-600"), title: "Built for Interviews", desc: "Bosses and missions are designed around the mistakes people make in real interviews: joins, filters, grouping, and logic." }
                ].map((item, i) => (
                  <div key={i} className={`rounded-2xl border p-5 backdrop-blur-xl transition-colors ${t("border-white/10 bg-black/30", "border-slate-200 bg-white shadow-lg shadow-slate-200/50")}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <item.icon size={16} className={item.color} />
                      <div className={`text-[11px] font-black tracking-widest uppercase opacity-80 ${item.color}`}>
                        {item.title}
                      </div>
                    </div>
                    <p className={`text-sm leading-relaxed ${t("text-white/70", "text-slate-600")}`}>
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <NeonGradientCard className="rounded-2xl" isDark={isDark} borderColors="from-emerald-400 via-cyan-500 to-blue-600">
                  <button
                    onClick={handleStartGame}
                    className={`w-full h-full flex items-center justify-center gap-3 px-8 py-4 text-sm md:text-base font-black text-white transition-colors ${t("bg-slate-900 hover:bg-slate-800", "bg-slate-900 hover:bg-slate-800")}`}
                  >
                    ENTER THE CASINO <ArrowRight className="w-20 h-5 text-emerald-400" />
                  </button>
                </NeonGradientCard>

                <button
                  onClick={() => document.getElementById("video-player")?.scrollIntoView({ behavior: "smooth" })}
                  className={`flex items-center justify-center gap-2 rounded-2xl border px-7 py-4 text-sm md:text-base font-black transition-colors ${t("border-white/10 bg-white/5 hover:bg-white/10 text-white/90", "border-slate-200 bg-white hover:bg-slate-50 text-slate-700 shadow-sm")}`}
                >
                  <Video className={`w-9 h-5 ${t("text-yellow-200", "text-fuchsia-500")}`} />
                  See how it works
                </button>
              </div>
            </div>

            {/* RIGHT: Slot Demo (Theme Aware) */}
            <NeonGradientCard className="rounded-[2rem]" isDark={isDark} borderColors="from-yellow-400 via-red-500 to-fuchsia-600">
              <div
                className={`relative p-6 md:p-8 overflow-hidden transition-colors duration-500 ${t("bg-black/55", "bg-slate-900")}`} // Kept dark in light mode for screen contrast
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                }}
                onMouseEnter={() => setIsHoveringSlot(true)}
                onMouseLeave={() => setIsHoveringSlot(false)}
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.4)_100%)]" />

                {/* Cursor Glow */}
                {isHoveringSlot && (
                  <div
                    className="pointer-events-none absolute inset-0 transition-opacity duration-200"
                    style={{
                      background: `radial-gradient(320px circle at ${mousePos.x}px ${mousePos.y}px, rgba(250,204,21,0.15), transparent 60%)`,
                    }}
                  />
                )}

                {/* Scanlines */}
                <div
                  className="pointer-events-none absolute inset-0 opacity-[0.10] mix-blend-overlay"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(180deg, rgba(255,255,255,0.20) 0px, rgba(255,255,255,0.20) 1px, transparent 2px, transparent 6px)",
                  }}
                />

                <div className="relative z-10 space-y-6">
                  <div className="flex justify-between items-center border-b border-white/10 pb-4">
                    <span className="text-[10px] font-black text-yellow-200/80 uppercase tracking-[0.35em]">
                      Live Demo
                    </span>
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_5px_red]"></div>
                      <div className="w-2 h-2 rounded-full bg-yellow-500 shadow-[0_0_5px_yellow]"></div>
                      <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_5px_green]"></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 text-center font-mono text-xs md:text-sm">
                    {reels.map((term, index) => (
                      <div
                        key={index}
                        className={[
                          "rounded-xl border border-white/10 bg-black py-6 md:py-7 font-black text-yellow-100",
                          "shadow-[inset_0_0_20px_rgba(0,0,0,1)] flex items-center justify-center h-20 md:h-24",
                          isSpinning ? "animate-pulse blur-[0.6px] text-yellow-200/40" : "",
                        ].join(" ")}
                      >
                        <span className="drop-shadow-[0_0_5px_rgba(253,224,71,0.6)]">{term}</span>
                      </div>
                    ))}
                  </div>

                  <div className="text-center bg-black/50 rounded-xl p-4 border border-emerald-500/20">
                    <div className="text-[10px] uppercase text-emerald-500 font-black tracking-widest mb-1">
                      Resulting Query
                    </div>
                    <code className="text-sm text-emerald-400 font-mono block truncate drop-shadow-[0_0_5px_rgba(52,211,153,0.5)]">
                      {reels.join(" ")}
                    </code>
                  </div>

                  <button
                    onClick={spinSlot}
                    disabled={isSpinning}
                    className={`w-full rounded-2xl py-3 text-[11px] md:text-sm font-black uppercase tracking-[0.25em] transition-all border
                        ${isSpinning ? "bg-black/30 border-white/10 text-white/35 cursor-not-allowed" : "bg-gradient-to-r from-yellow-600/20 to-fuchsia-600/20 border-yellow-500/30 text-white hover:border-yellow-400 hover:shadow-[0_0_20px_rgba(250,204,21,0.2)]"}`}
                  >
                    {isSpinning ? "Simulating..." : "🎰 Test Spin Logic"}
                  </button>

                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="text-[10px] font-black tracking-[0.35em] uppercase text-white/60 mb-2">
                      What happens in the game
                    </div>
                    <div className="space-y-2 text-sm text-white/70">
                      <div className="flex items-start gap-2">
                        <span className="mt-1 h-2 w-2 rounded-full bg-yellow-300 shadow-[0_0_12px_rgba(250,204,21,0.8)]" />
                        <span>You spin to generate a query, then answer a logic question about it.</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="mt-1 h-2 w-2 rounded-full bg-fuchsia-300 shadow-[0_0_12px_rgba(236,72,153,0.8)]" />
                        <span>Correct answers pay chips + CP. CP unlocks missions and boss fights.</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="mt-1 h-2 w-2 rounded-full bg-cyan-300 shadow-[0_0_12px_rgba(34,211,238,0.8)]" />
                        <span>Spend winnings on Rig upgrades that permanently boost your odds.</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </NeonGradientCard>
          </div>

          {/* FULL WIDTH SECTION - WHAT YOU GET */}
          <div className="max-w-7xl mx-auto mt-14">
            <div className={`rounded-2xl border p-8 backdrop-blur-xl ${t("border-yellow-300/15 bg-gradient-to-r from-yellow-500/10 via-black/30 to-fuchsia-500/10", "border-slate-200 bg-white/60")}`}>
              <div className={`text-[11px] font-black tracking-[0.35em] uppercase mb-6 text-center ${t("text-yellow-200/80", "text-slate-500")}`}>
                What you get in one session
              </div>
              <div className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-sm ${t("text-white/75", "text-slate-600")}`}>
                {[
                  "Real query recognition (not memorization)",
                  "Targeted weak-area practice via missions",
                  "Daily rewards to keep you consistent",
                  "Rig upgrades that make progress visible"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-2">
                    <CheckCircle size={20} className="text-emerald-500 shrink-0" />
                    <span className="font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* --- TRUSTED BY --- */}
        <section className={`border-y py-10 transition-colors ${t("border-white/10 bg-black/25", "border-slate-200 bg-white")}`}>
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <p className={`text-[10px] font-black uppercase tracking-[0.35em] mb-8 ${t("text-white/55", "text-slate-400")}`}>
              Helping learners stay consistent
            </p>
            <div className={`flex flex-wrap justify-center items-center gap-10 lg:gap-16 opacity-50 grayscale hover:grayscale-0 transition-all duration-500 ${t("text-white", "text-slate-800")}`}>
              {['TECH_CORP', 'DATA_FLOW', 'PY_SYSTEMS', 'SQL_MASTERS', 'CLOUD_NINE'].map((logo, i) => (
                <h3 key={i} className="text-xl font-black tracking-tighter flex items-center gap-2">
                  {logo.includes('SQL') && <Database size={18} />} {logo}
                </h3>
              ))}
            </div>
          </div>
        </section>

        {/* --- FEATURES --- */}
        <section id="features" className="py-20 md:py-24 max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-14">
            <h2 className={`text-3xl md:text-4xl font-black mb-4 ${t("text-white", "text-slate-900")}`}>
              Core Mechanics (and why they work)
            </h2>
            <p className={`max-w-3xl mx-auto leading-relaxed ${t("text-white/70", "text-slate-500")}`}>
              This isn’t “learning with decoration.” The game loop is built to force
              query understanding, reward consistency, and make progress visible.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {[
              { icon: Database, color: "text-emerald-500", title: "Syntax Slots", desc: "Reels generate real SQL patterns: selecting columns, filtering rows, grouping, ordering, and limiting. You aren’t memorizing commands — you’re learning what the query means.", borderColor: "from-emerald-500 via-teal-500 to-green-500", extra: "Example: GROUP BY + HAVING = filtering aggregates" },
              { icon: Skull, color: "text-red-500", title: "Boss Battles", desc: "Bosses represent common failure modes: infinite loops of confusion, join explosions, deadlocks, and logic traps. Boss fights train speed + accuracy under pressure.", borderColor: "from-red-500 via-orange-500 to-red-600", extra: "Boss tip: bank chips before Level 5/10/15" },
              { icon: Cpu, color: "text-yellow-500", title: "Rig Progression", desc: "Upgrades make the grind feel rewarding. CPUs multiply wins, GPUs raise luck, RAM reduces lifeline costs, and PSUs can refund lost bets. Strategy matters.", borderColor: "from-yellow-400 via-orange-400 to-yellow-500", extra: "Snowball plan: get a CPU upgrade early" }
            ].map((f, i) => (
              <NeonGradientCard key={i} className="rounded-3xl" isDark={isDark} borderColors={f.borderColor}>
                <div className="p-7">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${t("bg-white/5 border border-white/10", "bg-slate-50 border border-slate-200")}`}>
                    <f.icon size={30} className={f.color} />
                  </div>
                  <h3 className={`text-xl font-black mb-2 ${t("text-white", "text-slate-900")}`}>{f.title}</h3>
                  <p className={`text-sm leading-relaxed ${t("text-white/70", "text-slate-600")}`}>{f.desc}</p>
                  <div className={`mt-4 text-xs font-mono p-2 rounded-lg inline-block ${t("text-white/60", "text-slate-500 bg-slate-100")}`}>
                    {f.extra}
                  </div>
                </div>
              </NeonGradientCard>
            ))}
          </div>

          <div className="mt-10 grid lg:grid-cols-2 gap-6">
            <NeonGradientCard className="rounded-3xl" isDark={isDark} borderColors="from-fuchsia-500 via-purple-500 to-pink-500">
              <div className="p-7">
                <div className="flex items-center gap-2 mb-3">
                  <Star size={16} className="text-fuchsia-500" />
                  <div className="text-[11px] font-black tracking-[0.35em] uppercase text-fuchsia-600">
                    Why this keeps you consistent
                  </div>
                </div>
                <p className={`text-sm leading-relaxed ${t("text-white/70", "text-slate-600")}`}>
                  Consistency beats intensity. Daily flips and daily challenges give you a simple
                  reason to return. Even on busy days, you can log in, flip 4 cards, and keep your
                  learning streak alive.
                </p>
                <div className={`mt-4 grid sm:grid-cols-2 gap-3 text-sm ${t("text-white/75", "text-slate-700")}`}>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    <span>Daily flips: guaranteed chips</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    <span>Short sessions: 5–15 minutes</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    <span>Progress visible: CP + levels</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    <span>Rewards loop: upgrades + missions</span>
                  </div>
                </div>
              </div>
            </NeonGradientCard>

            <div className={`rounded-3xl border p-7 shadow-lg ${t("border-white/10 bg-black/30 backdrop-blur-xl", "border-slate-200 bg-white")}`}>
              <div className="flex items-center gap-2 mb-3">
                <Trophy size={16} className="text-yellow-500" />
                <div className={`text-[11px] font-black tracking-[0.35em] uppercase ${t("text-yellow-200/80", "text-yellow-600")}`}>
                  High Roller Strategy (simple)
                </div>
              </div>
              <div className={`space-y-4 text-sm ${t("text-white/70", "text-slate-600")}`}>
                <div className="flex gap-3">
                  <div className={`mt-0.5 h-6 w-6 rounded-full border flex items-center justify-center text-[11px] font-black ${t("bg-white/5 border-white/10 text-white", "bg-slate-100 border-slate-200 text-slate-700")}`}>
                    1
                  </div>
                  <div>
                    <div className={`font-black ${t("text-white", "text-slate-900")}`}>Start with daily flips</div>
                    <div className={t("text-white/70", "text-slate-500")}>
                      Get guaranteed chips, then do conservative spins. Avoid tilt.
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className={`mt-0.5 h-6 w-6 rounded-full border flex items-center justify-center text-[11px] font-black ${t("bg-white/5 border-white/10 text-white", "bg-slate-100 border-slate-200 text-slate-700")}`}>
                    2
                  </div>
                  <div>
                    <div className={`font-black ${t("text-white", "text-slate-900")}`}>Buy a CPU upgrade early</div>
                    <div className={t("text-white/70", "text-slate-500")}>
                      Multipliers apply to all wins. This is the fastest snowball route.
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className={`mt-0.5 h-6 w-6 rounded-full border flex items-center justify-center text-[11px] font-black ${t("bg-white/5 border-white/10 text-white", "bg-slate-100 border-slate-200 text-slate-700")}`}>
                    3
                  </div>
                  <div>
                    <div className={`font-black ${t("text-white", "text-slate-900")}`}>Bosses = skill checks</div>
                    <div className={t("text-white/70", "text-slate-500")}>
                      Boss fights reveal weak areas. Use them as practice, not punishment.
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate("/instructions")}
                className={`mt-6 w-full rounded-2xl border px-5 py-3 text-[11px] font-black tracking-[0.25em] uppercase transition-colors ${t("border-white/10 bg-white/5 hover:bg-white/10 text-white/85", "border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600")}`}
              >
                Open Full Guide
              </button>
            </div>
          </div>
        </section>

        {/* --- TESTIMONIALS --- */}
        <section className={`py-20 border-y ${t("border-white/10 bg-black/25", "border-slate-200 bg-white")}`}>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className={`text-3xl font-black ${t("text-white", "text-slate-900")}`}>Player Stories</h2>
              <p className={`mt-3 max-w-2xl mx-auto ${t("text-white/70", "text-slate-500")}`}>
                The best feedback we get is when learners stick with it and start feeling
                confident with queries.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {TESTIMONIALS.map((testimonial, idx) => (
                <div
                  key={idx}
                  className={`rounded-3xl border p-6 transition-all ${t("border-white/10 bg-black/30 hover:border-yellow-200/25", "border-slate-200 bg-white hover:shadow-xl shadow-sm")}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`text-sm font-black ${t("text-white", "text-slate-900")}`}>{testimonial.name}</div>
                    <span className={`text-[10px] font-black tracking-widest uppercase px-3 py-1 rounded-full ${t("bg-yellow-400/10 border border-yellow-200/15 text-yellow-200", "bg-cyan-50 text-cyan-700")}`}>
                      Outcome
                    </span>
                  </div>
                  <div className={`text-[11px] font-black tracking-[0.25em] uppercase mb-3 ${t("text-white/55", "text-slate-400")}`}>
                    {testimonial.role}
                  </div>
                  <p className={`text-sm leading-relaxed ${t("text-white/70", "text-slate-600")}`}>“{testimonial.quote}”</p>
                  <div className={`mt-4 text-sm font-black ${t("text-emerald-200", "text-emerald-600")}`}>
                    {testimonial.outcome}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* --- ROADMAP --- */}
        <section id="roadmap" className="py-20 md:py-24 max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className={`text-3xl md:text-4xl font-black mb-4 ${t("text-white", "text-slate-900")}`}>
              What’s Coming Next
            </h2>
            <p className={`max-w-3xl mx-auto leading-relaxed ${t("text-white/70", "text-slate-500")}`}>
              Casino Royale is the foundation. We’re extending the same game loop into
              Python, analytics, and GenAI — without losing the fun.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <NeonGradientCard className="rounded-3xl" isDark={isDark} borderColors="from-cyan-600 via-blue-600 to-cyan-500">
              <div className="p-7">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen size={18} className="text-cyan-500" />
                  <div className="text-[11px] font-black tracking-[0.35em] uppercase text-cyan-600">
                    Learning Tracks
                  </div>
                </div>
                <ul className={`space-y-3 text-sm ${t("text-white/70", "text-slate-600")}`}>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    SQL Foundations → Advanced
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    Python for Analytics
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    Projects + Interview Prep
                  </li>
                </ul>
              </div>
            </NeonGradientCard>

            <NeonGradientCard className="rounded-3xl" isDark={isDark} borderColors="from-yellow-600 via-orange-600 to-yellow-500">
              <div className="p-7">
                <div className="flex items-center gap-2 mb-3">
                  <Server size={18} className="text-yellow-500" />
                  <div className="text-[11px] font-black tracking-[0.35em] uppercase text-yellow-600">
                    New Game Modes
                  </div>
                </div>
                <ul className={`space-y-3 text-sm ${t("text-white/70", "text-slate-600")}`}>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    Raid Bosses (team play)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    Timed Challenges (speed rounds)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    Prestige Worlds (SQLite → Postgres → Oracle)
                  </li>
                </ul>
              </div>
            </NeonGradientCard>

            <NeonGradientCard className="rounded-3xl" isDark={isDark} borderColors="from-fuchsia-600 via-pink-600 to-fuchsia-500">
              <div className="p-7">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={18} className="text-fuchsia-500" />
                  <div className="text-[11px] font-black tracking-[0.35em] uppercase text-fuchsia-600">
                    Career Rewards
                  </div>
                </div>
                <ul className={`space-y-3 text-sm ${t("text-white/70", "text-slate-600")}`}>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    Workshop unlocks via gems
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    Certificates + skill proof
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    Community events + live sessions
                  </li>
                </ul>
              </div>
            </NeonGradientCard>
          </div>

          <div className={`mt-10 rounded-3xl border p-7 backdrop-blur-xl ${t("border-yellow-200/15 bg-gradient-to-r from-yellow-500/10 via-black/30 to-fuchsia-500/10", "border-yellow-200/50 bg-gradient-to-r from-yellow-50 via-white to-fuchsia-50 shadow-sm")}`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className={`text-[10px] font-black tracking-[0.35em] uppercase mb-2 ${t("text-yellow-200/80", "text-yellow-600")}`}>
                  Ready to enter?
                </div>
                <div className={`text-2xl font-black ${t("text-white", "text-slate-900")}`}>
                  Start with a few spins, earn upgrades, and feel the difference.
                </div>
                <div className={`mt-2 ${t("text-white/70", "text-slate-500")}`}>
                  You don’t need long study sessions. You need a loop you’ll actually repeat.
                </div>
              </div>
              <button
                onClick={handleStartGame}
                className={`shrink-0 px-7 py-4 rounded-2xl font-black tracking-widest uppercase transition-all active:scale-[0.99] ${t("bg-gradient-to-r from-yellow-300 via-yellow-200 to-fuchsia-200 text-black shadow-[0_0_40px_rgba(250,204,21,0.22)] hover:shadow-[0_0_55px_rgba(250,204,21,0.30)]", "bg-gradient-to-r from-slate-900 to-slate-800 text-white shadow-xl hover:shadow-2xl")}`}
              >
                Play Now
              </button>
            </div>
          </div>
        </section>

        {/* --- FAQ & VIDEO SECTION --- */}
        <section id="video-player" className="py-20 md:py-24 max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-20 items-start">

            {/* LEFT: FAQ Questions */}
            <div>
              <h2 className={`text-3xl font-black text-left mb-10 ${t("text-white", "text-slate-900")}`}>
                Common Questions
              </h2>

              <div className="space-y-4">
                {FAQS.map((f, i) => {
                  const isOpen = openFAQ === f.question;
                  return (
                    <div key={i} className={`overflow-hidden rounded-2xl border transition-all duration-300
                        ${isOpen
                        ? t("border-yellow-200/25 bg-black/50 shadow-[0_0_15px_rgba(250,204,21,0.1)]", "border-yellow-400 bg-white shadow-md")
                        : t("border-white/10 bg-black/25", "border-slate-200 bg-white")}`}>
                      <button
                        onClick={() => setOpenFAQ((prev) => (prev === f.question ? null : f.question))}
                        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                      >
                        <span className={`text-sm font-black ${isOpen ? t("text-yellow-200", "text-slate-900") : t("text-white/85", "text-slate-700")}`}>
                          {f.question}
                        </span>
                        <ChevronDown size={18} className={`transition-transform duration-300 ${isOpen ? "rotate-180 text-yellow-500" : "text-slate-400"}`} />
                      </button>

                      <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                        <div className="overflow-hidden">
                          <div className={`border-t px-6 py-5 text-sm leading-relaxed ${t("border-white/10 text-white/70", "border-slate-100 text-slate-600 bg-slate-50/50")}`}>
                            {f.answer}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RIGHT: Video Player */}
            <div className="space-y-6">
              <div className="text-center lg:text-left space-y-5">
                <h3 className="text-xl md:text-2xl font-black text-red-500 tracking-wide uppercase">
                  How to Play SQL SLOT
                </h3>
              </div>

              <NeonGradientCard className="rounded-3xl" isDark={isDark} borderColors="from-red-500 via-orange-500 to-yellow-500">
                <div ref={playerContainerRef} className={`p-1 flex flex-col h-full ${t("bg-black", "bg-white")}`}>
                  {/* Window Header / Controls */}
                  <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center bg-inherit z-50">
                    <span className={`font-black text-lg ${t("text-white", "text-slate-900")}`}>DataSense Casino Royale</span>
                    <div className="flex items-center gap-2">
                      {/* Maximize / Minimize Buttons */}
                      <button
                        onClick={toggleFullscreen}
                        className={`p-2 rounded-full hover:bg-white/10 transition-colors ${t("text-white", "text-slate-900")}`}
                        title={isFullscreen ? "Minimize" : "Maximize"}
                      >
                        {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                      </button>
                      {/* Close Button */}
                      <button
                        onClick={handleStopVideo}
                        className="p-2 rounded-full hover:bg-red-500/20 text-red-500 transition-colors"
                        title="Close Video"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="relative aspect-video bg-black rounded-b-2xl overflow-hidden group flex-1">
                    <video
                      ref={videoRef}
                      src="/video/dsslot.mp4"
                      className="w-full h-full object-cover"
                      onEnded={() => setIsVideoPlaying(false)}
                      onClick={toggleVideo}
                    />

                    {/* Custom Play/Pause Overlay */}
                    <div className={`absolute inset-0 flex items-center justify-center bg-black/40 transition-all duration-300
                          ${isVideoPlaying ? "opacity-0 hover:opacity-100" : "opacity-100"}`}>
                      <button
                        onClick={toggleVideo}
                        className="h-16 w-16 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:scale-110 transition-all hover:bg-white/20 shadow-xl"
                      >
                        {isVideoPlaying ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" className="ml-1" />}
                      </button>
                    </div>
                  </div>
                </div>
              </NeonGradientCard>
            </div>

          </div>
        </section>
      </main>

      {/* --- LIVE TICKER --- */}
      <div className={`fixed bottom-0 left-0 right-0 z-40 border-t py-2 hidden md:block backdrop-blur-xl ${t("border-yellow-200/15 bg-black/35", "border-slate-200 bg-white/90")}`}>
        <div className="max-w-7xl mx-auto px-6 overflow-hidden">
          <div className={`flex items-center gap-10 whitespace-nowrap animate-[ticker_26s_linear_infinite] text-[11px] font-mono ${t("text-yellow-100/80", "text-slate-600")}`}>
            {Array.from({ length: 2 }).flatMap((_, loopIdx) =>
              marqueeItems.map((m, idx) => (
                <span key={`${loopIdx}-${idx}`} className="inline-flex items-center gap-2">
                  <span className="text-yellow-500">{m.icon}</span>
                  <span className="font-bold">{m.text}</span>
                  <span className="mx-4 opacity-30">•</span>
                </span>
              ))
            )}
          </div>
        </div>
      </div>

      {/* --- FOOTER --- */}
      <footer className={`border-t pt-14 pb-28 ${t("bg-black/80 border-white/10", "bg-slate-900 border-slate-800")}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 grid md:grid-cols-4 gap-10 mb-10 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Database className="text-yellow-400" size={20} />
              <span className="font-black text-white uppercase tracking-widest">DataSense</span>
            </div>
            <p className="text-white/60 leading-relaxed">Gamifying data education for the next generation of engineers. Not a real gambling site. All currency is virtual.</p>
          </div>

          <div>
            <h4 className="text-white font-black mb-4 uppercase text-[11px] tracking-[0.25em]">
              Product
            </h4>
            <ul className="space-y-2 text-white/60">
              <li>
                <button onClick={() => navigate("/instructions")} className="hover:text-yellow-200 transition-colors">Game Guide</button>
              </li>
              <li>
                <button onClick={() => navigate("/missions")} className="hover:text-yellow-200 transition-colors">Missions</button>
              </li>
              <li>
                <button onClick={() => navigate("/challenges")} className="hover:text-yellow-200 transition-colors">Daily Challenges</button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-black mb-4 uppercase text-[11px] tracking-[0.25em]">
              Resources
            </h4>
            <ul className="space-y-2 text-white/60">
              <li className="hover:text-yellow-200 transition-colors cursor-default">SQL Cheat Sheet</li>
              <li className="hover:text-yellow-200 transition-colors cursor-default">Community</li>
              <li className="hover:text-yellow-200 transition-colors cursor-default">Support</li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-black mb-4 uppercase text-[11px] tracking-[0.25em]">
              Social
            </h4>
            <div className="flex gap-4 text-white/55">
              <Twitter className="hover:text-cyan-200 cursor-pointer" size={20} />
              <Github className="hover:text-white cursor-pointer" size={20} />
              <Linkedin className="hover:text-cyan-200 cursor-pointer" size={20} />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 border-t border-white/10 pt-8 text-center text-xs text-white/45">
          © {new Date().getFullYear()} DataSense Casino Royale. Educational only. No real money.
        </div>
      </footer>

      <style>{`@keyframes ticker { 0% { transform: translateX(0%); } 100% { transform: translateX(-50%); } }`}</style>
    </div>
  );
}

export default LandingPage;